import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Stethoscope,
  Package,
  Receipt,
  MapPin,
  UserCog,
  UserPlus,
  BarChart3,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Building2,
  Settings,
  ShieldAlert,
  ScanLine,
  FileText
} from 'lucide-react';

const Sidebar = ({ collapsed, onToggle, onNavigate, showCloseButton = true }) => {
  const { user, clinic, logout, isSuperAdmin, isClinicAdmin, isDoctor, isFrontDesk } = useAuth();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState(() => ({ 'Forms': location.pathname.startsWith('/jotform') || location.pathname.startsWith('/patient-intake-submissions') }));

  const toggleMenu = (name) => setOpenMenus(prev => ({ ...prev, [name]: !prev[name] }));

  const navigation = [
    // Super Admin only - Clinics management
    { name: 'Clinics', href: '/clinics', icon: Building2, roles: ['super_admin'] },
    { name: 'Login Audit', href: '/login-audit', icon: ShieldAlert, roles: ['super_admin'] },
    // All roles except super_admin and inventory_manager get dashboard
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['clinic_admin', 'front_desk', 'nurse'] },
    { name: 'Patients', href: '/patients', icon: Users, roles: ['clinic_admin', 'front_desk', 'nurse'] },
    { name: 'Visits', href: '/visits', icon: Calendar, roles: ['clinic_admin', 'doctor', 'front_desk', 'nurse'] },
    { name: 'Services', href: '/services', icon: Stethoscope, roles: ['clinic_admin'] },
    { name: 'Inventory', href: '/inventory', icon: Package, roles: ['clinic_admin', 'inventory_manager'] },
    { name: 'Scanner', href: '/mobile-inventory', icon: ScanLine, roles: ['inventory_manager'] },
    { name: 'Billing', href: '/billing', icon: Receipt, roles: ['clinic_admin', 'front_desk'] },
    { name: 'Locations', href: '/locations', icon: MapPin, roles: ['clinic_admin'] },
    { name: 'Users', href: '/users', icon: UserCog, roles: ['super_admin', 'clinic_admin'] },
    { name: 'Employees', href: '/employees', icon: UserPlus, roles: ['clinic_admin'] },
    { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['clinic_admin', 'front_desk'] },
    {
      name: 'Forms', icon: FileText, roles: ['clinic_admin', 'front_desk'],
      children: [
        ...(clinic?.has_jotform_configured ? [{ name: 'JotForm Submissions', href: '/jotform' }] : []),
        { name: 'Patient Intake', href: '/patient-intake-submissions' },
      ]
    },
    { name: 'Settings', href: '/settings', icon: Settings, roles: ['clinic_admin'] },
    { name: 'Platform', href: '/settings', icon: Settings, roles: ['super_admin'] },
  ];

  const filteredNav = navigation.filter(item => item.roles.includes(user?.role));

  const handleNavClick = () => {
    // Close sidebar when clicking a nav item (for doctors)
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} flex flex-col`}>
      {/* Toggle Button - positioned at middle right edge of sidebar */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-teal-200 rounded-full flex items-center justify-center text-teal-600 hover:text-teal-700 hover:bg-teal-50 transition-colors shadow-lg z-50"
        data-testid="sidebar-toggle-btn"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
      
      {/* Logo - white panel so colored/JPG logos display cleanly */}
      <div className="h-16 flex items-center justify-center border-b border-white/20 px-3 bg-white/10 backdrop-blur-sm">
        <div className={`flex items-center justify-center rounded-lg bg-white shadow-sm ${collapsed ? 'h-10 w-10 p-1' : 'h-12 w-full max-w-[190px] px-3 py-1.5'}`}>
          <img 
            src={clinic?.logo_url || "/images/logo.png"} 
            alt={clinic?.name || "Clinic"} 
            className="max-h-full max-w-full object-contain"
            onError={(e) => { e.target.onerror = null; e.target.src = "/images/logo.png"; }}
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        <ul className="space-y-1">
          {filteredNav.map((item) => (
            <li key={item.name}>
              {item.children ? (
                // Expandable submenu
                <>
                  <button
                    onClick={() => {
                      if (collapsed) { onToggle && onToggle(); }
                      toggleMenu(item.name);
                    }}
                    className={`sidebar-link w-full ${collapsed ? 'justify-center px-0' : ''} ${item.children.some(c => location.pathname.startsWith(c.href)) ? 'active' : ''}`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    title={collapsed ? item.name : undefined}
                  >
                    {collapsed ? (
                      <item.icon className="w-5 h-5" />
                    ) : (
                      <>
                        <item.icon className="w-5 h-5" />
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${openMenus[item.name] ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </button>
                  {!collapsed && openMenus[item.name] && (
                    <ul className="mt-1 ml-4 pl-3 border-l border-white/15 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <NavLink
                            to={child.href}
                            onClick={handleNavClick}
                            className={({ isActive }) =>
                              `sidebar-link text-sm py-2 ${isActive ? 'active' : ''}`
                            }
                            data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <span>{child.name}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : item.href === '/mobile-inventory' ? (
                // Scanner link opens in same tab (full screen experience)
                <a
                  href={item.href}
                  onClick={handleNavClick}
                  className={`sidebar-link ${collapsed ? 'justify-center px-0' : ''}`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {!collapsed && <span>{item.name}</span>}
                </a>
              ) : (
                <NavLink
                  to={item.href}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`
                  }
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {!collapsed && <span>{item.name}</span>}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="border-t border-white/20 p-4 bg-white/5">
        {!collapsed && (
          <div className="mb-3">
            <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-white/60 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        )}
        <button
          onClick={logout}
          className={`flex items-center gap-3 px-4 py-3 text-white/80 hover:bg-red-500/20 hover:text-red-200 transition-all duration-200 rounded-xl w-full ${collapsed ? 'justify-center px-0' : ''}`}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      {/* Collapse toggle - only show for non-doctors */}
      {showCloseButton && (
        <button
          onClick={onToggle}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-teal-200 rounded-full flex items-center justify-center text-teal-600 hover:bg-teal-50 hover:text-teal-700 transition-colors shadow-md"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}
    </aside>
  );
};

export default Sidebar;
